import React, { ErrorInfo, ReactNode } from 'react';
import { Environment, RelayEnvironmentProvider } from 'react-relay';
import { logCheckoutError } from './helpers/logCheckoutError';
import CheckoutErrorPage from './components/containers/CheckoutErrorPage';
import CheckoutMutationWrapper from './mutations/CheckoutMutationWrapper';
import { getBuyerId } from 'dibs-cookie-jar';
import { getGuestUserId } from './helpers/checkoutGuestUserToken';
import {
    setPrerenderedCheckoutRootState,
    PRE_RENDER_STATE_ERROR,
} from './prerender/prerenderedCheckoutStore';
import CheckoutPageHeader from './header/CheckoutPageHeader';
import { CheckoutMobilePageHeader } from './header/CheckoutMobilePageHeader';

interface ErrorBoundaryProps {
    children: ReactNode;
    relayEnvironment: Environment;
    itemId: string;
    isMobile: boolean;
}

interface ErrorBoundaryState {
    error: Error | null;
}

export class CheckoutErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { error };
    }

    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { error: null };
    }

    componentDidCatch(error: Error, info: ErrorInfo): void {
        setPrerenderedCheckoutRootState(PRE_RENDER_STATE_ERROR);
        // datadog error reporting
        logCheckoutError(error, 'fatal', { componentStack: info.componentStack });
        const buyerId = getBuyerId(document.cookie) || getGuestUserId();
        if (buyerId) {
            // backend boss service error reporting
            CheckoutMutationWrapper.commit(
                'ReportCheckoutMutation',
                this.props.relayEnvironment,
                {
                    itemId: this.props.itemId,
                    buyerId,
                    type: 'CHECKOUT_ERROR',
                },
                this.props.isMobile
            ).catch(() => {
                // ignore errors
            });
        }
    }

    render(): ReactNode {
        if (this.state.error) {
            return (
                <RelayEnvironmentProvider environment={this.props.relayEnvironment}>
                    {this.props.isMobile ? (
                        <CheckoutMobilePageHeader viewer={null} buyer={null} item={null} />
                    ) : (
                        <CheckoutPageHeader viewer={null} buyer={null} />
                    )}
                    <CheckoutErrorPage error={this.state.error} />;
                </RelayEnvironmentProvider>
            );
        }

        return this.props.children;
    }
}
